import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateCartItem, getCartTotal } = useCart();

  const handleQuantityChange = (cartItemId, newQuantity) => {
    if (newQuantity > 0) {
      updateCartItem(cartItemId, { quantity: newQuantity });
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="bg-navy text-gray-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Your Cart is Empty</h1>
          <p className="text-gray-500 mb-8">Add some products to get started!</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary hover:bg-primary-dark text-white font-medium py-3 px-8 rounded-md transition"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-navy text-gray-800 min-h-screen py-10">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cartItems.map(item => (
                <div key={item.cartItemId} className="bg-navy-light border border-primary/20 rounded-lg p-6 flex gap-6">
                  {/* Product Image */}
                  <div className="w-24 h-24 bg-navy rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.image || item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                    <div className="text-sm text-gray-500 space-y-1 mb-4">
                      <p>Size: <span className="text-gray-700">{item.selectedSize}</span></p>
                      {item.selectedColor && (
                        <p>Color: <span className="text-gray-700 capitalize">{item.selectedColor}</span></p>
                      )}
                      <p>Price: <span className="text-primary font-semibold">${item.price?.toFixed(2)}</span></p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center border border-primary/30 rounded-md">
                        <button
                          onClick={() => handleQuantityChange(item.cartItemId, item.quantity - 1)}
                          className="p-1 hover:bg-primary/10 transition"
                        >
                          <Minus size={18} />
                        </button>
                        <span className="px-4 py-1 font-medium">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.cartItemId, item.quantity + 1)}
                          className="p-1 hover:bg-primary/10 transition"
                        >
                          <Plus size={18} />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeFromCart(item.cartItemId)}
                        className="text-red-600 hover:text-red-800 transition ml-auto"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Line Total */}
                  <div className="text-right flex flex-col justify-center">
                    <p className="text-sm text-gray-500">Subtotal</p>
                    <p className="text-2xl font-bold text-primary">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <div className="bg-navy-light border border-primary/20 rounded-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6 border-b border-primary/20 pb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600">${getCartTotal().toFixed(2) > 75 ? 'Free' : '5'}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="flex justify-between text-xl font-bold mb-6">
                <span>Total</span>
                <span className="text-primary">${getCartTotal().toFixed(2)}</span>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 rounded-md transition mb-3"
              >
                Proceed to Checkout
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full border border-primary/30 text-gray-600 hover:text-primary font-medium py-3 rounded-md transition"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;